import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';

import type { Score } from '#src/components/scoring/Score/index.tsx';
import { ScoreByMode } from '#src/components/scoring/ScoreByMode/index.tsx';
import type { TotalAveragePlayerScore } from '#src/lib/averagePlayerScore.ts';

import type { CardScore_anyCard } from './__generated__/index.graphql.ts';

type Props = {
  card: CardScore_anyCard;
  mode: TotalAveragePlayerScore;
  remainingPoints?: number;
  hideTooltip?: boolean;
  small?: boolean;
} & Pick<
  ComponentProps<typeof Score>,
  'animated' | 'isCappedMode' | 'shape' | 'size'
>;

export const CardScore = ({
  card,
  mode,
  isCappedMode,
  remainingPoints,
  animated,
  hideTooltip,
  shape = 'HEXAGON',
  small,
}: Props) => {
  const scores = {
    LAST_FORTY_SO5_AVERAGE_SCORE: card.lastFortySo5AverageScore,
    LAST_TEN_PLAYED_SO5_AVERAGE_SCORE: card.lastTenPlayedSo5AverageScore,
    LAST_FIVE_SO5_AVERAGE_SCORE: card.lastFiveSo5AverageScore,
    LAST_FIFTEEN_SO5_AVERAGE_SCORE: card.lastFifteenSo5AverageScore,
    SEASON_AVERAGE_SCORE: card.seasonAverage,
  };

  return (
    <ScoreByMode
      hideTooltip={hideTooltip}
      shape={shape}
      scores={scores}
      mode={mode}
      isCappedMode={isCappedMode}
      animated={animated}
      remainingPoints={remainingPoints}
      size={small ? 3 : undefined}
    />
  );
};

CardScore.fragments = {
  anyCard: gql`
    fragment CardScore_anyCard on AnyCardInterface {
      slug
      seasonAverage: averageScore(type: SEASON_AVERAGE_SCORE)
      lastFiveSo5AverageScore: averageScore(type: LAST_FIVE_SO5_AVERAGE_SCORE)
      lastTenPlayedSo5AverageScore: averageScore(
        type: LAST_TEN_PLAYED_SO5_AVERAGE_SCORE
      )
      lastFifteenSo5AverageScore: averageScore(type: LAST_FIFTEEN_SO5_AVERAGE_SCORE)
      lastFortySo5AverageScore: averageScore(type: LAST_FORTY_SO5_AVERAGE_SCORE)
    }
  ` as TypedDocumentNode<CardScore_anyCard>,
};
